import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { AlertTriangle, Info, Send } from 'lucide-react';
import type { BatchDetail } from '@autopilot/contracts';
import { api, fetchAllPages } from '../../api/client';
import { qk, useRoles } from '../../api/hooks';
import { isConflict, type ApiError } from '../../api/errors';
import { DistributionChart } from '../../components/DistributionChart';
import { ScoreThreshold } from '../../components/ScoreThreshold';
import { ErrorAlert, LoadingState } from '../../components/States';
import { distribution, NO_VALID_KEY, NO_VALID_LABEL, SCORE_BUCKETS, startButtonLabel } from '../../lib/scores';
import { useIdempotencyKey } from '../../lib/idempotency';
import { reasonLabel } from '../../lib/labels';

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Serverseitige Verteilung (BatchDetail.distributions, Index 10 = ohne gültige Bewertung) in Balken umwandeln. */
export function barsFromServer(counts: readonly number[]) {
  return [
    ...SCORE_BUCKETS.map((b, i) => ({ key: b.key, label: b.label, count: counts[i] ?? 0, muted: false })),
    { key: NO_VALID_KEY, label: NO_VALID_LABEL, count: counts[10] ?? 0, muted: true },
  ];
}

export function ScoreDistributions({ batch }: { batch: BatchDetail }) {
  const server = batch.distributions;
  // Fallback nur, wenn das Backend keine Verteilung liefert: alle Ticketseiten laden.
  const all = useQuery({
    queryKey: [...qk.allScores(batch.id), batch.rowVersion],
    queryFn: () => fetchAllPages((cursor) => api.listTickets(batch.id, { cursor, limit: 200, sort: 'nummer' })),
    placeholderData: keepPreviousData,
    enabled: !server,
  });
  const rows = all.data ?? [];
  const corr = useMemo(
    () => (server ? barsFromServer(server.correctness) : distribution(rows, 'correctness').map((b) => ({ ...b, muted: b.key === 'none' }))),
    [server, rows],
  );
  const risk = useMemo(
    () => (server ? barsFromServer(server.errorRisk) : distribution(rows, 'errorRisk').map((b) => ({ ...b, muted: b.key === 'none' }))),
    [server, rows],
  );
  if (!server && all.isPending) return <LoadingState label="Verteilungen werden berechnet …" />;
  if (!server && all.isError) return <ErrorAlert error={all.error} action="Verteilungen laden" />;
  const total = server ? server.correctness.reduce((a, b) => a + b, 0) : rows.length;
  return (
    <Box sx={{ display: 'grid', gap: 4, gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <DistributionChart title="Verteilung Richtigkeit" bars={corr} total={total} testId="dist-correctness" />
      <DistributionChart title="Verteilung Fehlerrisiko" bars={risk} total={total} testId="dist-errorrisk" />
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: -2 }}>
        Alle {total.toLocaleString('de-DE')} Tickets des Batches; ausgeschlossene, nicht bewertete und veraltete Bewertungen zählen als „ohne gültige Bewertung“.
      </Typography>
    </Box>
  );
}

/**
 * Regler, Live-Zähler (serverseitig über /eligibility, entprellt) und Startbutton.
 * Kein Start durch Reglerbewegung; Start sendet die gesehene Zahl als expectedEligibleCount.
 */
export function ReviewControls({
  batch,
  minCorrectness,
  maxErrorRisk,
  onChange,
  canStart,
  onStarted,
}: {
  batch: BatchDetail;
  minCorrectness: number;
  maxErrorRisk: number;
  onChange: (min: number, max: number) => void;
  canStart: boolean;
  onStarted: () => void;
}) {
  const qc = useQueryClient();
  const { isOperator, me } = useRoles();
  const dMin = useDebounced(minCorrectness);
  const dMax = useDebounced(maxErrorRisk);
  const pending = dMin !== minCorrectness || dMax !== maxErrorRisk;
  const [conflictNotice, setConflictNotice] = useState<false | { serverCount: number | null }>(false);
  const idem = useIdempotencyKey();

  const elig = useQuery({
    queryKey: [...qk.eligibility(batch.id, dMin, dMax), batch.rowVersion],
    queryFn: ({ signal }) => api.eligibility(batch.id, dMin, dMax, signal),
    placeholderData: keepPreviousData,
  });

  const start = useMutation({
    mutationFn: () => {
      const e = elig.data!;
      const body = { expectedRowVersion: e.batchRowVersion, minCorrectness: dMin, maxErrorRisk: dMax, expectedEligibleCount: e.eligibleCount };
      return api.createExecutionRun(batch.id, body, idem.keyFor({ id: batch.id, ...body }));
    },
    onSuccess: () => {
      idem.reset();
      setConflictNotice(false);
      void qc.invalidateQueries({ queryKey: qk.batch(batch.id) });
      onStarted();
    },
    onError: (err) => {
      if (isConflict(err)) {
        idem.reset();
        const data = (err as ApiError).problem as { data?: { eligibleCount?: number } } | null;
        setConflictNotice({ serverCount: typeof data?.data?.eligibleCount === 'number' ? data.data.eligibleCount : null });
        void qc.invalidateQueries({ queryKey: qk.batch(batch.id) });
      }
    },
  });

  const count = elig.data?.eligibleCount ?? 0;
  const total = elig.data?.total ?? batch.ticketCount;
  let disabledReason: string | null = null;
  if (!isOperator) disabledReason = 'Nur Operatoren können die Ausführung starten.';
  else if (me && !me.executionEnabled) disabledReason = 'Globaler Not-Stopp aktiv – es kann nichts versendet werden.';
  else if (pending || elig.isFetching) disabledReason = 'Anzahl wird aktualisiert …';
  else if (elig.isError) disabledReason = 'Anzahl konnte nicht ermittelt werden.';
  else if (count === 0) disabledReason = 'Kein Ticket erfüllt die Kriterien. Passen Sie die Schwellen an, prüfen Sie Vorschläge erneut oder schließen Sie keine weiteren Tickets aus.';

  const blocked = Object.entries(elig.data?.blockedByReason ?? {}).filter(([, n]) => n > 0);

  return (
    <Card component="section" aria-labelledby="criteria-title">
      <CardContent sx={{ p: 3 }}>
        <Typography id="criteria-title" variant="h5" component="h2">
          Kriterien für die Ausführung
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 2.5, display: 'flex', gap: 0.75, alignItems: 'center' }}>
          <Info size={16} aria-hidden /> Unabhängige KI-Bewertungen, keine garantierten Wahrscheinlichkeiten.
        </Typography>
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          <ScoreThreshold
            label="Richtigkeit mindestens"
            value={minCorrectness}
            onChange={(v) => onChange(v, maxErrorRisk)}
            disabled={!canStart}
            direction="min"
            helper="Grenzwert eingeschlossen (≥)."
            testId="threshold-correctness"
          />
          <ScoreThreshold
            label="Fehlerrisiko höchstens"
            value={maxErrorRisk}
            onChange={(v) => onChange(minCorrectness, v)}
            disabled={!canStart}
            direction="max"
            helper="Grenzwert eingeschlossen (≤)."
            testId="threshold-errorrisk"
          />
        </Box>
        <Box sx={{ mt: 3, p: 2, borderRadius: '12px', backgroundColor: 'var(--color-primary-soft)', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h6" component="p" aria-live="polite" data-testid="eligible-counter" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {elig.isPending ? 'Wird berechnet …' : `${count.toLocaleString('de-DE')} von ${total.toLocaleString('de-DE')} Tickets erfüllen die Kriterien`}
          </Typography>
          {(pending || elig.isFetching) && <CircularProgress size={16} aria-label="Wird aktualisiert" />}
        </Box>
        {blocked.length > 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }} data-testid="blocked-reasons">
            Nicht ausführbar: {blocked.map(([k, n]) => `${reasonLabel(k)} ${n}`).join(' · ')}. Technische und fachliche Sperren gelten unabhängig von den Schwellen.
          </Typography>
        )}
        {conflictNotice && (
          <Alert severity="warning" icon={<AlertTriangle size={20} />} sx={{ mt: 2 }} onClose={() => setConflictNotice(false)} data-testid="start-conflict">
            Die Ergebnisse haben sich seit Ihrer Ansicht geändert{conflictNotice.serverCount !== null ? ` (aktuell ${conflictNotice.serverCount} ausführbare Tickets)` : ''}. Die Ansicht wurde neu geladen – bitte prüfen Sie die aktuelle Anzahl und starten Sie erneut. Es wurde nichts versendet.
          </Alert>
        )}
        {canStart && isOperator && (
          <Box sx={{ mt: 2.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={start.isPending ? <CircularProgress size={16} color="inherit" /> : <Send size={18} />}
              disabled={!!disabledReason || start.isPending}
              onClick={() => start.mutate()}
              data-testid="start-execution"
              aria-describedby={disabledReason ? 'start-disabled-reason' : 'start-effect'}
            >
              {startButtonLabel(count)}
            </Button>
            <Typography id={disabledReason ? 'start-disabled-reason' : 'start-effect'} variant="body2" sx={{ color: 'text.secondary', maxWidth: 560 }}>
              {disabledReason ?? 'Versendet die freigegebenen Antworten über Nidus und setzt die Tickets auf „Gelöst“. Versandte Antworten können nicht zurückgenommen werden.'}
            </Typography>
          </Box>
        )}
        {!isConflict(start.error) && (
          <Box sx={{ mt: 2 }}>
            <ErrorAlert error={start.error} action="Ausführung starten" onClose={() => start.reset()} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
